<!-- ==================== Hero ==================== -->
<section class="home-hero" aria-labelledby="home-hero-title">
  <h1 class="sr-only" id="home-hero-title" translate="hero.bannerTitle"></h1>
  <picture>
    <source media="(max-width: 767px)" [attr.srcset]="heroSrc().mobile" />
    <img
      class="home-hero__img"
      [src]="heroSrc().desktop"
      [alt]="'hero.bannerTitle' | translate"
      width="1600"
      height="556"
      fetchpriority="high"
    />
  </picture>
</section>

<!-- ==================== Libraries ==================== -->
<section class="home-section home-libraries" aria-labelledby="home-libraries-title">
  <div class="home-section__inner">
    <div class="home-section__header">
      <h2 class="home-section__title" id="home-libraries-title" translate="homeNew.maeenLibraries"></h2>
      <a class="home-section__link" routerLink="/libraries" translate="home.viewAll"></a>
    </div>

    @if (topLibrariesError()) {
      <div class="alert alert-warning text-center mb-0" translate="errors.dataUnavailable"></div>
    } @else if (topLibraries$ | async; as libraries) {
      @if (libraries.length) {
        <div class="home-libraries__grid">
          @for (library of libraries; track library.library_id; let i = $index) {
            <app-home-library-card
              [library]="library"
              [fallbackImage]="libraryFallbackImages[i % libraryFallbackImages.length]"
            />
          }
        </div>
      }
    } @else {
      <app-skeleton-card [count]="3" />
    }
  </div>
</section>

<!-- ==================== Latest additions ==================== -->
<section class="home-section home-latest" aria-labelledby="home-latest-title">
  <img class="home-latest__pattern" src="images/custom/home-books-pattern.png" alt="" aria-hidden="true" loading="lazy" />

  <div class="home-section__inner">
    <div class="home-section__header">
      <h2 class="home-section__title" id="home-latest-title" translate="homeNew.latestAdditions"></h2>
      <a class="home-section__link home-section__link--disabled" aria-disabled="true" tabindex="-1" translate="home.viewAll"></a>
    </div>

    @if (latestBibliosError()) {
      <div class="alert alert-warning text-center mb-0" translate="errors.dataUnavailable"></div>
    } @else if (latestBiblios$ | async; as biblios) {
      @if (biblios.length) {
        <div class="home-latest__carousel">
          <swiper-container
            #latestSwiper
            class="home-latest__swiper"
            space-between="16"
            breakpoints='{"0": {"slidesPerView": 1, "slidesPerGroup": 1}, "768": {"slidesPerView": 2, "slidesPerGroup": 2}, "992": {"slidesPerView": 3, "slidesPerGroup": 3}}'
            (swiperafterinit)="syncLatestState(latestSwiper)"
            (swiperlock)="syncLatestState(latestSwiper)"
            (swiperunlock)="syncLatestState(latestSwiper)"
            (swiperbreakpoint)="syncLatestState(latestSwiper)"
            (swiperresize)="syncLatestState(latestSwiper)"
            (swiperslidechange)="syncLatestState(latestSwiper)"
            (swipersnapindexchange)="syncLatestState(latestSwiper)"
            (swipertoedge)="syncLatestState(latestSwiper)"
            (swiperfromedge)="syncLatestState(latestSwiper)"
          >
            @for (biblio of biblios; track biblio.id) {
              <swiper-slide>
                <app-home-biblio-card [item]="biblio" />
              </swiper-slide>
            }
          </swiper-container>

          @if (!latestCarouselLocked()) {
          <div class="home-latest__controls">
            <!-- RTL places the first child on the right, so the arrows lead. -->
            <div class="home-latest__arrows">
              <button
                type="button"
                class="home-arrow home-arrow--next"
                [disabled]="latestAtEnd()"
                [attr.aria-label]="'homeNew.nextSlide' | translate"
                (click)="slideNext(latestSwiper)"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                  <path d="M8.90998 20.67C8.71998 20.67 8.52998 20.6 8.37998 20.45C8.08998 20.16 8.08998 19.68 8.37998 19.39L14.9 12.87C15.38 12.39 15.38 11.61 14.9 11.13L8.37998 4.61C8.08998 4.32 8.08998 3.84 8.37998 3.55C8.66998 3.26 9.14998 3.26 9.43998 3.55L15.96 10.07C16.47 10.58 16.76 11.27 16.76 12C16.76 12.73 16.48 13.42 15.96 13.93L9.43998 20.45C9.28998 20.59 9.09998 20.67 8.90998 20.67Z" fill="currentColor" />
                </svg>
              </button>

              <button
                type="button"
                class="home-arrow home-arrow--prev"
                [disabled]="latestAtStart()"
                [attr.aria-label]="'homeNew.previousSlide' | translate"
                (click)="slidePrev(latestSwiper)"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                  <path d="M15 20.67C14.81 20.67 14.62 20.6 14.47 20.45L7.95003 13.93C6.89003 12.87 6.89003 11.13 7.95003 10.07L14.47 3.55C14.76 3.26 15.24 3.26 15.53 3.55C15.82 3.84 15.82 4.32 15.53 4.61L9.01003 11.13C8.53003 11.61 8.53003 12.39 9.01003 12.87L15.53 19.39C15.82 19.68 15.82 20.16 15.53 20.45C15.38 20.59 15.19 20.67 15 20.67Z" fill="currentColor" />
                </svg>
              </button>
            </div>

            <div class="home-latest__dots">
              @for (page of latestPages(); track page) {
                <button
                  type="button"
                  class="home-latest__dot"
                  [class.is-active]="page === latestActivePage()"
                  [attr.aria-current]="page === latestActivePage() ? 'true' : null"
                  [attr.aria-label]="('homeNew.goToPage' | translate) + ' ' + (page + 1)"
                  (click)="goToLatestPage(latestSwiper, page)"
                ></button>
              }
            </div>
          </div>
          }
        </div>
      }
    } @else {
      <app-skeleton-card [count]="3" />
    }
  </div>
</section>

<!-- ==================== Photo gallery ==================== -->
<section class="home-section home-gallery" aria-labelledby="home-gallery-title">
  <img class="home-gallery__bg" src="images/custom/home-gallery-bg.png" alt="" aria-hidden="true" loading="lazy" />

  <div class="home-section__inner">
    <div class="home-section__header">
      <h2 class="home-section__title home-section__title--on-color" id="home-gallery-title" translate="homeNew.photoGallery"></h2>

      @if (!galleryCarouselLocked()) {
      <div class="home-gallery__nav">
        <button
          type="button"
          class="home-arrow home-arrow--ghost"
          [attr.aria-label]="'homeNew.nextSlide' | translate"
          (click)="slideNext(gallerySwiper)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <path d="M8.90998 20.67C8.71998 20.67 8.52998 20.6 8.37998 20.45C8.08998 20.16 8.08998 19.68 8.37998 19.39L14.9 12.87C15.38 12.39 15.38 11.61 14.9 11.13L8.37998 4.61C8.08998 4.32 8.08998 3.84 8.37998 3.55C8.66998 3.26 9.14998 3.26 9.43998 3.55L15.96 10.07C16.47 10.58 16.76 11.27 16.76 12C16.76 12.73 16.48 13.42 15.96 13.93L9.43998 20.45C9.28998 20.59 9.09998 20.67 8.90998 20.67Z" fill="currentColor" />
          </svg>
        </button>

        <button
          type="button"
          class="home-arrow home-arrow--ghost"
          [attr.aria-label]="'homeNew.previousSlide' | translate"
          (click)="slidePrev(gallerySwiper)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <path d="M15 20.67C14.81 20.67 14.62 20.6 14.47 20.45L7.95003 13.93C6.89003 12.87 6.89003 11.13 7.95003 10.07L14.47 3.55C14.76 3.26 15.24 3.26 15.53 3.55C15.82 3.84 15.82 4.32 15.53 4.61L9.01003 11.13C8.53003 11.61 8.53003 12.39 9.01003 12.87L15.53 19.39C15.82 19.68 15.82 20.16 15.53 20.45C15.38 20.59 15.19 20.67 15 20.67Z" fill="currentColor" />
          </svg>
        </button>
      </div>
      }
    </div>

    <swiper-container
      #gallerySwiper
      class="home-gallery__swiper"
      pagination="true"
      pagination-clickable="true"
      space-between="20"
      breakpoints='{"0": {"slidesPerView": 1, "slidesPerGroup": 1}, "768": {"slidesPerView": 2, "slidesPerGroup": 2}, "992": {"slidesPerView": 3, "slidesPerGroup": 3}}'
      (swiperafterinit)="syncGalleryLock(gallerySwiper)"
      (swiperlock)="syncGalleryLock(gallerySwiper)"
      (swiperunlock)="syncGalleryLock(gallerySwiper)"
      (swiperbreakpoint)="syncGalleryLock(gallerySwiper)"
      (swiperresize)="syncGalleryLock(gallerySwiper)"
    >
      @for (photo of galleryPhotos; track photo.src) {
        <swiper-slide>
          <figure class="home-gallery__card">
            <img [src]="photo.src" [alt]="photo.altKey | translate" loading="lazy" />
          </figure>
        </swiper-slide>
      }
    </swiper-container>
  </div>
</section>

<!-- ==================== Working hours ==================== -->
<section class="home-section home-hours" aria-labelledby="home-hours-title">
  <img class="home-hours__pattern" src="images/custom/home-books-pattern.png" alt="" aria-hidden="true" loading="lazy" />
  <span class="home-hours__glow" aria-hidden="true"></span>
  <span class="home-hours__rings" aria-hidden="true"></span>

  <div class="home-section__inner home-hours__inner">
    <p class="home-hours__badge">
      <span class="home-hours__badge-dot" aria-hidden="true"></span>
      <span translate="homeNew.startJourney"></span>
    </p>

    <h2 class="home-hours__title" id="home-hours-title" translate="homeNew.workingHours"></h2>
    <p class="home-hours__desc" translate="homeNew.workingHoursDesc"></p>

    @if (kawLibrary$ | async; as kaw) {
      @if (kaw.library_hours?.length) {
        <div class="home-hours__times">
          @for (hour of kaw.library_hours; track $index) {
            @if (hour.open_time && hour.close_time) {
              <p class="home-hours__time">
                @if (hour.start_day === hour.end_day) {
                  {{ 'days.single' | translate: { day: ('days.' + hour.start_day | translate) } }}
                } @else {
                  {{ 'days.range' | translate: { start: ('days.' + hour.start_day | translate), end: ('days.' + hour.end_day | translate) } }}
                }
                {{ 'homeNew.hoursFrom' | translate }}
                <strong>{{ formatTime(hour.open_time).h }}:{{ formatTime(hour.open_time).m }} {{ 'time.' + formatTime(hour.open_time).ampm | translate }}</strong>
                {{ 'homeNew.hoursTo' | translate }}
                <strong>{{ formatTime(hour.close_time).h }}:{{ formatTime(hour.close_time).m }} {{ 'time.' + formatTime(hour.close_time).ampm | translate }}</strong>
              </p>
            }
          }
        </div>
      }
    }

    <a class="home-hours__cta" routerLink="/contact-us" translate="homeNew.contactUs"></a>
  </div>
</section>
